"use client";

import Link from "next/link";
import { Title } from "@/components/common/typography/Title";
import { Icon } from "@iconify/react";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import { Toaster, useToaster } from "@/components/common/Toaster";
import { usePathname, useSearchParams } from "next/navigation";
import {
  CONTACT_INTENT_EVENT,
  readContactIntent,
  type ContactIntent,
} from "@/lib/contact-intent";

const offerOptions = [
  { value: "open-space-partage", label: "Open space partagé (22 postes)" },
  { value: "bureaux-fermes", label: "Bureaux fermés (3 à 10 postes)" },
  { value: "plateau-privatif", label: "Plateau privatif (20 postes)" },
  { value: "etage-entier", label: "Étage entier (30 postes)" },
  { value: "salles-reunion", label: "Salles de réunion (3 à 16 personnes)" },
  { value: "autre", label: "Autre demande" },
];

const requestTypeOptions = [
  { value: "visite-guidee", label: "Visite guidée" },
  { value: "essai-gratuit", label: "Période d'essai gratuite" },
  { value: "devis", label: "Recevoir un devis" },
  { value: "disponibilite", label: "Vérifier la disponibilité" },
  { value: "rappel", label: "Être rappelé(e)" },
];

const contactSchema = z.object({
  offerType: z
    .string()
    .min(1, "Sélectionnez l'offre qui vous intéresse")
    .max(80, "Valeur d'offre invalide"),
  requestType: z
    .string()
    .min(1, "Sélectionnez le type de demande")
    .max(80, "Type de demande invalide"),
  name: z
    .string()
    .min(2, "Le nom doit contenir au moins 2 caractères")
    .max(50, "Le nom ne peut pas dépasser 50 caractères")
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, "Le nom contient des caractères non valides"),
  company: z
    .string()
    .max(80, "Le nom de société ne peut pas dépasser 80 caractères")
    .optional()
    .or(z.literal("")),
  email: z
    .string()
    .email("Format d'email invalide")
    .regex(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Format d'email invalide",
    ),
  phone: z
    .string()
    .regex(
      /^(\+33|0)[1-9](\d{8}|\s\d{2}\s\d{2}\s\d{2}\s\d{2})$/,
      "Format de téléphone invalide",
    )
    .optional()
    .or(z.literal("")),
  teamSize: z
    .string()
    .max(30, "La taille d'équipe ne peut pas dépasser 30 caractères")
    .optional()
    .or(z.literal("")),
  moveInDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date invalide")
    .optional()
    .or(z.literal("")),
  message: z
    .string()
    .max(1000, "Le message ne peut pas dépasser 1000 caractères"),
  honeypot: z.string().max(0).optional(),
  source: z.string().max(120).optional(),
});

type ContactForm = z.infer<typeof contactSchema>;

const fieldClassName =
  "w-full rounded-xl border border-black/15 bg-white px-4 py-3 text-[15px] outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20";

export const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toasts, addToast, removeToast } = useToaster();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      offerType: "",
      requestType: "",
    },
  });

  useEffect(() => {
    const applyIntent = (
      intent: Pick<ContactIntent, "offerType" | "requestType" | "source">,
    ) => {
      const validOfferValues = new Set(
        offerOptions.map((option) => option.value),
      );
      const validRequestValues = new Set(
        requestTypeOptions.map((option) => option.value),
      );

      const preferredOfferType = intent.offerType?.trim() || "";
      const preferredRequestType = intent.requestType?.trim() || "";
      const preferredSource = intent.source?.trim() || "";

      if (validOfferValues.has(preferredOfferType)) {
        setValue("offerType", preferredOfferType);
      }

      if (validRequestValues.has(preferredRequestType)) {
        setValue("requestType", preferredRequestType);
      }

      setValue("source", preferredSource || pathname || "/");
    };

    const offerTypeParam = searchParams.get("offerType")?.trim() || "";
    const requestTypeParam = searchParams.get("requestType")?.trim() || "";
    const sourceParam = searchParams.get("source")?.trim() || "";
    const savedIntent = readContactIntent();

    applyIntent({
      offerType: savedIntent?.offerType || offerTypeParam,
      requestType: savedIntent?.requestType || requestTypeParam,
      source: savedIntent?.source || sourceParam,
    });

    const hasVisibleTrackingParam =
      searchParams.has("offerType") ||
      searchParams.has("requestType") ||
      searchParams.has("source");

    if (hasVisibleTrackingParam && typeof window !== "undefined") {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("offerType");
      currentUrl.searchParams.delete("requestType");
      currentUrl.searchParams.delete("source");
      const nextSearch = currentUrl.searchParams.toString();
      const cleanUrl = `${currentUrl.pathname}${nextSearch ? `?${nextSearch}` : ""}${currentUrl.hash}`;
      window.history.replaceState({}, "", cleanUrl);
    }

    const onContactIntentUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<ContactIntent>;
      if (!customEvent.detail) return;

      applyIntent({
        offerType: customEvent.detail.offerType,
        requestType: customEvent.detail.requestType,
        source: customEvent.detail.source,
      });
    };

    window.addEventListener(
      CONTACT_INTENT_EVENT,
      onContactIntentUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        CONTACT_INTENT_EVENT,
        onContactIntentUpdate as EventListener,
      );
    };
  }, [pathname, searchParams, setValue]);

  const onSubmit = async (data: ContactForm) => {
    try {
      setIsSubmitting(true);
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Une erreur est survenue");
      }

      if (result.previewMode && result.previewHtml) {
        const previewWindow = window.open("", "_blank", "noopener,noreferrer");
        if (previewWindow) {
          previewWindow.document.open();
          previewWindow.document.write(result.previewHtml);
          previewWindow.document.close();
        }
        addToast("Aperçu email ouvert (mode test Resend).", "success");
      } else {
        addToast("Message envoyé avec succès !", "success");
      }

      reset({
        offerType: "",
        requestType: "",
        source: window.location.pathname || "/",
      });
    } catch (error) {
      addToast(
        error instanceof Error ? error.message : "Une erreur est survenue",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const address = "Espace Cadet, 5 rue Marguerite de Rochechouart, 75009 Paris";

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      addToast("Adresse copiée", "success");
    } catch (error) {
      console.error("Erreur lors de la copie :", error);
    }
  };

  return (
    <section
      className="flex flex-col gap-12 px-4 py-20 ph:px-24 md:px-48 lg:px-72 xl:px-96"
      id="contact"
    >
      <Toaster toasts={toasts} removeToast={removeToast} />
      <Title title="Contactez-nous" />

      <div className="grid grid-cols-1 gap-8 text-black xl:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-3xl border border-black/10 bg-white/80 p-5 shadow-lg shadow-black/5 sm:p-7 md:p-8">
          <div className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 p-4">
            <p className="text-sm font-semibold text-black">
              Décrivez votre besoin et nous revenons vers vous rapidement avec
              une proposition adaptée.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <input
              type="text"
              style={{ display: "none" }}
              {...register("honeypot")}
            />
            <input type="hidden" {...register("source")} />

            <div>
              <label
                htmlFor="offerType"
                className="mb-1.5 block text-sm font-semibold"
              >
                Offre qui vous intéresse <span className="text-primary">*</span>
              </label>
              <select
                id="offerType"
                {...register("offerType")}
                className={fieldClassName}
                defaultValue=""
              >
                <option value="" disabled>
                  Choisir une offre
                </option>
                {offerOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.offerType && (
                <p className="mt-1.5 text-sm text-red-500">
                  {errors.offerType.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="requestType"
                className="mb-1.5 block text-sm font-semibold"
              >
                Type de demande <span className="text-primary">*</span>
              </label>
              <select
                id="requestType"
                {...register("requestType")}
                className={fieldClassName}
                defaultValue=""
              >
                <option value="" disabled>
                  Choisir une option
                </option>
                {requestTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.requestType && (
                <p className="mt-1.5 text-sm text-red-500">
                  {errors.requestType.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="name"
                  className="mb-1.5 block text-sm font-semibold"
                >
                  Nom <span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  {...register("name")}
                  className={fieldClassName}
                  placeholder="Votre nom"
                />
                {errors.name && (
                  <p className="mt-1.5 text-sm text-red-500">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="mb-1.5 block text-sm font-semibold"
                >
                  Société (optionnel)
                </label>
                <input
                  type="text"
                  id="company"
                  {...register("company")}
                  className={fieldClassName}
                  placeholder="Nom de votre société"
                />
                {errors.company && (
                  <p className="mt-1.5 text-sm text-red-500">
                    {errors.company.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-sm font-semibold"
                >
                  Email <span className="text-primary">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  {...register("email")}
                  className={fieldClassName}
                  placeholder="vous@entreprise.com"
                />
                {errors.email && (
                  <p className="mt-1.5 text-sm text-red-500">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="mb-1.5 block text-sm font-semibold"
                >
                  Téléphone (optionnel)
                </label>
                <input
                  type="tel"
                  id="phone"
                  {...register("phone")}
                  className={fieldClassName}
                  placeholder="06 00 00 00 00"
                />
                {errors.phone && (
                  <p className="mt-1.5 text-sm text-red-500">
                    {errors.phone.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="teamSize"
                  className="mb-1.5 block text-sm font-semibold"
                >
                  Taille d'équipe (optionnel)
                </label>
                <input
                  type="text"
                  id="teamSize"
                  {...register("teamSize")}
                  className={fieldClassName}
                  placeholder="Ex: 6 personnes"
                />
                {errors.teamSize && (
                  <p className="mt-1.5 text-sm text-red-500">
                    {errors.teamSize.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="moveInDate"
                  className="mb-1.5 block text-sm font-semibold"
                >
                  Date d'installation (optionnel)
                </label>
                <input
                  type="date"
                  id="moveInDate"
                  {...register("moveInDate")}
                  className={fieldClassName}
                />
                {errors.moveInDate && (
                  <p className="mt-1.5 text-sm text-red-500">
                    {errors.moveInDate.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="message"
                className="mb-1.5 block text-sm font-semibold"
              >
                Message (optionnel)
              </label>
              <textarea
                id="message"
                {...register("message")}
                rows={5}
                className={`${fieldClassName} min-h-[140px] resize-y`}
                placeholder="Décrivez votre besoin, vos contraintes et le délai souhaité."
              />
              {errors.message && (
                <p className="mt-1.5 text-sm text-red-500">
                  {errors.message.message}
                </p>
              )}
            </div>

            <div className="pt-2">
              <PrimaryButton
                text={isSubmitting ? "Envoi..." : "Envoyer ma demande"}
                className="w-full justify-center sm:w-auto"
              />
            </div>
          </form>
        </div>

        <aside className="flex flex-col gap-4 xl:sticky xl:top-28 xl:self-start">
          <div className="rounded-3xl border border-black/10 bg-white/85 p-5 shadow-lg shadow-black/5 sm:p-6">
            <h3 className="text-xl font-semibold">Parler à notre équipe</h3>
            <p className="mt-2 text-sm text-black/75">
              Réponse rapide pour disponibilité, tarifs et visite des espaces.
            </p>

            <div className="mt-5 space-y-3">
              <Link
                href="tel:+33610788013"
                className="flex items-center gap-3 rounded-xl border border-black/10 px-4 py-3 transition-colors hover:border-primary hover:text-primary"
              >
                <Icon icon="mdi:phone-outline" className="h-5 w-5" />
                <span>06 10 78 80 13</span>
              </Link>
              <Link
                href="https://wa.me/33610788013"
                target="_blank"
                className="flex items-center gap-3 rounded-xl border border-black/10 px-4 py-3 transition-colors hover:border-primary hover:text-primary"
              >
                <Icon icon="mdi:whatsapp" className="h-5 w-5" />
                <span>WhatsApp</span>
              </Link>
              <button
                type="button"
                onClick={copyAddress}
                className="flex w-full items-center gap-3 rounded-xl border border-black/10 px-4 py-3 text-left transition-colors hover:border-primary hover:text-primary"
              >
                <Icon icon="mdi:map-marker-outline" className="h-5 w-5" />
                <span className="text-sm">{address}</span>
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow-lg shadow-black/5">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.1532071325683!2d2.3452377!3d48.8777977!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e13c9c4d0fd%3A0x255a389738780479!2s5%20Rue%20Marguerite%20de%20Rochechouart%2C%2075009%20Paris!5e0!3m2!1sfr!2sfr!4v1710337849659!5m2!1sfr!2sfr"
              width="100%"
              height="360"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full"
              title="Carte Espace Cadet"
            />
          </div>
        </aside>
      </div>
    </section>
  );
};
